import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/Api';
import { api_url_v1 } from '../utils/Config';

const initialState = {
  items: [],
  deletedItems: [],
  selectedJob: null,
  meta: {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  },
  loading: false,
  error: null
};

export const fetchJobs = createAsyncThunk(
  'jobs/fetchJobs',
  async ({ page, limit, activeFilters, search }, { getState }) => {
    const { auth: { token } } = getState();
    const processedParams = {};
    for (const key in activeFilters) {
      processedParams[key] = Array.isArray(activeFilters[key]) 
        ? activeFilters[key].join(',') 
        : activeFilters[key];
    }
    const params = {
      sort:'-created_at',
      page,
      limit,
      search,
      ...processedParams
    
    }
    const response = await api.get(`${api_url_v1}/jobs`, {
      params,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    
    return {
      data: response.data.data || [],
      meta: response.data.meta || {
        current_page: page,
        last_page: 1,
        per_page: limit,
        total: 0
      }
    };
  }
);
export const fetchDeletedJobs = createAsyncThunk(
  'jobs/fetchDeletedJobs',
  async ({ page, limit, activeFilters, search }, { getState }) => {
    const { auth: { token } } = getState();
    const processedParams = {};
    for (const key in activeFilters) {
      processedParams[key] = Array.isArray(activeFilters[key]) 
        ? activeFilters[key].join(',') 
        : activeFilters[key];
    }
    const params = {
      page,
      limit,
      search,
      ...processedParams
    }
    const response = await api.get(`${api_url_v1}/jobs-deleted`, {
      params,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    
    return {
      data: response.data.data || [],
      meta: response.data.meta || {
        current_page: page,
        last_page: 1,
        per_page: limit,
        total: 0
      }
    };
  }
);

// export const fetchDeletedJobs = createAsyncThunk(
//   'jobs/fetchDeletedJobs',
//   async ({ page, limit }, { getState }) => {
//     const { auth: { token } } = getState();
//     const response = await api.get(`${api_url_v1}/jobs-deleted`, {
//       params: { page, limit },
//       headers: {
//         'Authorization': `Bearer ${token}`,
//         'Content-Type': 'application/json',
//       }
//     });
    
//     return {
//       data: response.data.data || [],
//       meta: response.data.meta || {
//         current_page: page,
//         last_page: 1,
//         per_page: limit,
//         total: 0
//       }
//     };
//   }
// );

export const createJob = createAsyncThunk(
  'jobs/createJob',
  async (jobData, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      const response = await api.post(`${api_url_v1}/jobs`, jobData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data' 
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Job creation failed');
    }
  }
);

export const fetchJobDetails = createAsyncThunk(
  'jobs/fetchJobDetails',
  async (id, { getState }) => {
    const { auth: { token } } = getState();
    const response = await api.get(`${api_url_v1}/jobs/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    return response.data;
  }
);
export const fetchDeletedJobDetails = createAsyncThunk(
  'jobs/fetchDeletedJobDetails',
  async (id, { getState }) => {
    const { auth: { token } } = getState();
    const response = await api.get(`${api_url_v1}/jobs/${id}/view-deleted`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    return response.data;
  }
);

export const updateJob = createAsyncThunk(
  'jobs/updateJob',
  async ({ id, data }, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      const response = await api.post(`${api_url_v1}/jobs/${id}`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        }
      });
      return { id, updatedJob: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Update failed');
    }
  }
);

export const deleteJob = createAsyncThunk(
  'jobs/deleteJob',
  async (id, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      await api.delete(`${api_url_v1}/jobs/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Delete failed');
    }
  }
);

export const restoreJob = createAsyncThunk(
  'jobs/restoreJob',
  async (id, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      const response = await api.put(`${api_url_v1}/jobs/${id}/restore`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Restore failed');
    }
  }
);

export const forceDeleteJob = createAsyncThunk(
  'jobs/forceDeleteJob',
  async (id, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      await api.delete(`${api_url_v1}/jobs/${id}/force-delete`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Force delete failed');
    }
  }
);

const jobsSlice = createSlice({
  name: 'jobsSlice',
  initialState,
  reducers: {
    setSelectedJob: (state, action) => {
      state.selectedJob = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Jobs Cases
      .addCase(fetchJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Fetch Deleted Jobs Cases
      .addCase(fetchDeletedJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeletedJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.deletedItems = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchDeletedJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch Job Details Cases
      .addCase(fetchJobDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedJob = action.payload.data;
      })
      .addCase(fetchJobDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      //fetch deleted job detail
      .addCase(fetchDeletedJobDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeletedJobDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedJob = action.payload.data;
      })
      .addCase(fetchDeletedJobDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Update Job Cases
      .addCase(updateJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateJob.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(job => job.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload.updatedJob;
        }
        if (state.selectedJob?.id === action.payload.id) {
          state.selectedJob = action.payload.updatedJob;
        }
      })
      .addCase(updateJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Update failed';
      })

      // Create Job Cases
      .addCase(createJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createJob.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(createJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Job creation failed';
      })

      // Delete Job Cases
      .addCase(deleteJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteJob.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(job => job.id !== action.payload);
        if (state.selectedJob?.id === action.payload) {
          state.selectedJob = null;
        }
        state.meta.total = Math.max(0, state.meta.total - 1);
      })
      .addCase(deleteJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Delete failed';
      })

      // Restore Job Cases
      .addCase(restoreJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreJob.fulfilled, (state, action) => {
        state.loading = false;
        // state.deletedItems = state.deletedItems.filter(job => job.id !== action.payload.id);
        // state.items.push(action.payload);
      })
      .addCase(restoreJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Restore failed';
      })

      // Force Delete Job Cases
      .addCase(forceDeleteJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forceDeleteJob.fulfilled, (state, action) => {
        state.loading = false;
        state.deletedItems = state.deletedItems.filter(job => job.id !== action.payload);
      })
      .addCase(forceDeleteJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Force delete failed';
      });
  }
});

export const { setSelectedJob } = jobsSlice.actions;
export default jobsSlice.reducer;